import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { AlertTriangle, TrendingUp, CheckCircle2, DollarSign, BarChart3, FileText, Users, Clock, Globe } from 'lucide-react';

const NAVY    = 'hsl(var(--c4p-navy-900))';
const ORANGE  = 'hsl(var(--c4p-orange-600))';
const PRODUCT_LABELS = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro', CUSTOM: 'C4P Custom', BESPOKE: 'C4P Custom', TEST_SYSTEM: 'C4P Custom (Test)' };
const STATUS_COLORS = { DRAFT: '#94a3b8', SENT: '#3b82f6', ACCEPTED: '#16a34a', EXPIRED: '#f59e0b' };

const reportsAPI = {
  summary:         (p) => apiClient.get('/reports/summary', { params: p }),
  overTime:        (p) => apiClient.get('/reports/over-time', { params: p }),
  byStatus:        (p) => apiClient.get('/reports/by-status', { params: p }),
  byCurrency:      (p) => apiClient.get('/reports/by-currency', { params: p }),
  byProduct:       (p) => apiClient.get('/reports/by-product', { params: p }),
  byUser:          (p) => apiClient.get('/reports/by-user', { params: p }),
  expiring:        (p) => apiClient.get('/reports/expiring-quotes', { params: p }),
  contractsSummary:(p) => apiClient.get('/reports/contracts-summary', { params: p }),
  byRegion:        (p) => apiClient.get('/reports/by-region', { params: p }),
  byCountry:       (p) => apiClient.get('/reports/by-country', { params: p }),
};

const fmt  = (n) => typeof n === 'number' ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '£0';
const fmtMo = (n) => fmt(n) + '/mo';
const pct  = (n) => `${Math.round((n || 0) * 100)}%`;

function KpiCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <CardContent className="p-4 space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</p>
          <Icon size={14} style={{ color: accent || NAVY, opacity: 0.7 }} />
        </div>
        <p className="text-2xl font-black tabular-nums" style={{ fontFamily: 'var(--font-display)', color: NAVY, borderLeft: `3px solid ${accent || NAVY}`, paddingLeft: '8px' }}>{value}</p>
        {sub && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{sub}</p>}
      </CardContent>
    </Card>
  );
}

function SimpleBar({ data, labelKey, valueKey, color = NAVY }) {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <p className="text-xs w-28 flex-shrink-0 truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{d[labelKey]}</p>
          <div className="flex-1 h-5 rounded-md overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
            <div className="h-full rounded-md transition-all"
              style={{ width: `${((d[valueKey] || 0) / max) * 100}%`, background: color, minWidth: d[valueKey] > 0 ? '4px' : '0' }} />
          </div>
          <span className="text-xs font-bold tabular-nums w-10 text-right" style={{ color: NAVY }}>{d[valueKey] || 0}</span>
        </div>
      ))}
    </div>
  );
}

function MonthlyChart({ data }) {
  const maxV = Math.max(...data.map(d => d.count || 0), 1);
  return (
    <div className="flex items-end gap-1.5 h-36">
      {data.map((d, i) => {
        const h = Math.round((d.count / maxV) * 100);
        const ah = d.count > 0 ? Math.round((d.accepted / d.count) * h) : 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
            <div className="w-full flex flex-col justify-end" style={{ height: '120px', gap: '1px' }}>
              <div style={{ height: `${h}%`, background: 'hsl(var(--muted))' }} className="rounded-t" />
              <div style={{ height: `${ah}%`, background: '#16a34a', marginTop: '-' + h + '%', position: 'relative', bottom: `${h}%` }}
                className="rounded-t absolute" />
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))', fontSize: '9px' }}>
              {d.month_label?.split(' ')[0]}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ── Reusable geography breakdown table ───────────────────────────────────────
function GeoTable({ title, subtitle, rows, labelKey, labelHeader, testId }) {
  const fmt  = (n) => n > 0 ? `£${Math.round(n || 0).toLocaleString('en-GB')}` : '—';
  const fmtMo = (n) => n > 0 ? `£${Math.round(n || 0).toLocaleString('en-GB')}/mo` : '—';

  if (!rows.length) return (
    <div className="rounded-xl border bg-white p-8 text-center">
      <Globe size={24} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No data available yet.</p>
    </div>
  );

  const totals = rows.reduce((acc, r) => ({
    count: acc.count + r.count, draft: acc.draft + r.draft, sent: acc.sent + r.sent,
    accepted: acc.accepted + r.accepted,
    pipeline_gbp: acc.pipeline_gbp + r.pipeline_gbp, won_gbp: acc.won_gbp + r.won_gbp,
    contracts: acc.contracts + r.contracts, mrr_gbp: acc.mrr_gbp + r.mrr_gbp,
  }), { count: 0, draft: 0, sent: 0, accepted: 0, pipeline_gbp: 0, won_gbp: 0, contracts: 0, mrr_gbp: 0 });

  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{title}</p>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{subtitle}</p>}
      </div>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
              <th className="text-left px-4 py-2 text-xs font-bold">{labelHeader}</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Total</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Draft</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Sent</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Won</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Win Rate</th>
              <th className="text-right px-3 py-2 text-xs font-bold">Pipeline GBP</th>
              <th className="text-right px-3 py-2 text-xs font-bold">Won GBP</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Contracts</th>
              <th className="text-right px-4 py-2 text-xs font-bold">MRR GBP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const winRate = r.count > 0 ? Math.round((r.accepted / r.count) * 100) : 0;
              return (
                <tr key={r[labelKey]} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                  <td className="px-4 py-2.5 font-semibold text-sm" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{r[labelKey] || 'Unknown'}</td>
                  <td className="text-center px-3 py-2.5 text-sm tabular-nums">{r.count}</td>
                  <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#94a3b8' }}>{r.draft || 0}</td>
                  <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#3b82f6' }}>{r.sent || 0}</td>
                  <td className="text-center px-3 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{r.accepted}</td>
                  <td className="text-center px-3 py-2.5">
                    <span className="text-xs font-bold" style={{ color: winRate >= 50 ? '#16a34a' : '#d97706' }}>{winRate}%</span>
                  </td>
                  <td className="text-right px-3 py-2.5 text-sm font-bold tabular-nums" style={{ color: r.pipeline_gbp > 0 ? 'hsl(var(--c4p-navy-900))' : 'hsl(var(--muted-fg))' }}>
                    {fmtMo(r.pipeline_gbp)}
                  </td>
                  <td className="text-right px-3 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{fmtMo(r.won_gbp)}</td>
                  <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: r.contracts > 0 ? '#0e7490' : 'hsl(var(--muted-fg))' }}>{r.contracts}</td>
                  <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#0e7490' }}>{fmtMo(r.mrr_gbp)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
              <td className="px-4 py-2.5 text-xs font-bold">TOTAL</td>
              <td className="text-center px-3 py-2.5 text-xs font-bold tabular-nums">{totals.count}</td>
              <td className="text-center px-3 py-2.5 text-xs tabular-nums">{totals.draft}</td>
              <td className="text-center px-3 py-2.5 text-xs tabular-nums">{totals.sent}</td>
              <td className="text-center px-3 py-2.5 text-xs font-bold tabular-nums">{totals.accepted}</td>
              <td className="text-center px-3 py-2.5 text-xs">{totals.count > 0 ? Math.round((totals.accepted/totals.count)*100) : 0}%</td>
              <td className="text-right px-3 py-2.5 text-xs font-bold tabular-nums">{fmtMo(totals.pipeline_gbp)}</td>
              <td className="text-right px-3 py-2.5 text-xs font-bold tabular-nums">{fmtMo(totals.won_gbp)}</td>
              <td className="text-center px-3 py-2.5 text-xs font-bold tabular-nums">{totals.contracts}</td>
              <td className="text-right px-4 py-2.5 text-xs font-bold tabular-nums">{fmtMo(totals.mrr_gbp)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { isAdmin, isManager, isAccounts, user } = useAuth();
  const isSales = !isAdmin && !isManager && !isAccounts;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const setTab = (t) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });

  // "My Reports / Team Reports" toggle — PSMs only.
  // ACCOUNTS, Managers, Admins always see full team data.
  const [teamMode, setTeamMode] = useState(false);
  const effectiveTeamMode = !isSales || isAccounts || teamMode;

  const [loading, setLoading]   = useState(true);
  const [summary, setSummary]   = useState(null);
  const [overTime, setOverTime] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [byCurrency, setByCurrency] = useState([]);
  const [byProduct, setByProduct]   = useState([]);
  const [byUser, setByUser]         = useState([]);
  const [expiring, setExpiring]     = useState([]);
  const [contracts, setContracts]   = useState(null);
  const [byRegion, setByRegion]     = useState([]);
  const [byCountry, setByCountry]   = useState([]);

  const p = effectiveTeamMode ? { team_mode: true } : {};

  useEffect(() => {
    setLoading(true);
    const calls = [
      reportsAPI.summary(p), reportsAPI.overTime({ months: 12, ...p }),
      reportsAPI.byStatus(p), reportsAPI.byCurrency(p),
      reportsAPI.byProduct(p), reportsAPI.expiring({ days: 30, ...p }),
      reportsAPI.contractsSummary(p), reportsAPI.byRegion(p), reportsAPI.byCountry(p),
    ];
    // Team tab: available to all when in team mode, only managers/admins otherwise
    if (effectiveTeamMode) calls.push(reportsAPI.byUser(p));
    Promise.allSettled(calls).then(results => {
      if (results[0].status === 'fulfilled') setSummary(results[0].value.data);
      if (results[1].status === 'fulfilled') setOverTime(results[1].value.data || []);
      if (results[2].status === 'fulfilled') setByStatus(results[2].value.data || []);
      if (results[3].status === 'fulfilled') setByCurrency(results[3].value.data || []);
      if (results[4].status === 'fulfilled') setByProduct(results[4].value.data || []);
      if (results[5].status === 'fulfilled') setExpiring(results[5].value.data || []);
      if (results[6].status === 'fulfilled') setContracts(results[6].value.data);
      if (results[7].status === 'fulfilled') setByRegion(results[7].value.data || []);
      if (results[8].status === 'fulfilled') setByCountry(results[8].value.data || []);
      if (effectiveTeamMode && results[9]?.status === 'fulfilled') setByUser(results[9].value.data || []);
      else if (!effectiveTeamMode) setByUser([]);
      setLoading(false);
    });
  }, [effectiveTeamMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const total    = summary?.total_quotes || 0;
  const accepted = summary?.by_status?.ACCEPTED || 0;

  const TABS = [
    { id: 'overview',   label: 'Overview',     icon: BarChart3 },
    { id: 'pipeline',   label: 'Pipeline',     icon: TrendingUp },
    { id: 'contracts',  label: 'Contracts MRR', icon: DollarSign },
    { id: 'expiring',   label: `Expiring (${expiring.length})`, icon: AlertTriangle },
    { id: 'geography',  label: 'Geography',    icon: Globe },
    // Team tab: always shown for managers/admins; shown to PSMs only in team mode
    ...(effectiveTeamMode ? [{ id: 'team', label: 'Team', icon: Users }] : []),
  ];

  return (
    <div className="flex flex-col min-h-full" data-testid="reports-page">
      {/* Header */}
      <div className="px-6 py-4 flex items-center gap-4 flex-shrink-0" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
        <BarChart3 size={18} style={{ color: NAVY, flexShrink: 0 }} />
        <div>
          <h1 className="text-xl font-black" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Reports & Analytics</h1>
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>All monetary values in GBP using daily FX rates</p>
        </div>
        {/* My Reports / Team Reports toggle — PSMs only; placed next to title for visibility */}
        {isSales && (
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg flex-shrink-0" style={{ background: '#e2e8f0' }} data-testid="reports-view-toggle">
            <button
              onClick={() => { setTeamMode(false); if (activeTab === 'team') setTab('overview'); }}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-all outline-none"
              style={{ background: !teamMode ? 'white' : 'transparent', color: !teamMode ? NAVY : '#64748b', boxShadow: !teamMode ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}
              data-testid="my-reports-toggle">
              My Reports
            </button>
            <button
              onClick={() => setTeamMode(true)}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-all outline-none"
              style={{ background: teamMode ? 'white' : 'transparent', color: teamMode ? NAVY : '#64748b', boxShadow: teamMode ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}
              data-testid="team-reports-toggle">
              Team Reports
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="px-6 flex gap-0.5 flex-shrink-0" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors outline-none"
            style={{
              color: activeTab === id ? NAVY : 'hsl(var(--muted-fg))',
              borderBottom: activeTab === id ? `2px solid ${NAVY}` : '2px solid transparent',
              marginBottom: '-1px', background: 'transparent',
            }}>
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
            <div className="grid grid-cols-2 gap-4">{[1,2].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
          </div>
        ) : (

          /* ══ OVERVIEW ══ */
          activeTab === 'overview' && (
            <div className="space-y-5 max-w-5xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={TrendingUp}   label="Total Pipeline (GBP)" value={fmtMo(summary?.pipeline_gbp || 0)}     sub="All currencies, monthly MRR" accent={NAVY}     testId="rpt-pipeline-gbp" />
                <KpiCard icon={CheckCircle2} label="Won Value (GBP)"      value={fmtMo(summary?.won_gbp || 0)}          sub="Monthly value of won quotes"  accent="#16a34a" testId="rpt-won-gbp" />
                <KpiCard icon={FileText}     label="Active Quotes"        value={total}                                 sub={`${accepted} won · ${total - accepted} in progress`}  accent="#d97706" testId="rpt-total" />
                <KpiCard icon={Clock}        label="Win Rate"             value={total > 0 ? pct(accepted/total) : '—'} sub={`${accepted} won of ${total} total`} accent="#0e7490" testId="rpt-conversion" />
              </div>
              {(summary?.expired_count > 0) && (
                <div className="rounded-xl border px-4 py-3 flex items-center justify-between gap-4"
                  style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} style={{ color: '#d97706', flexShrink: 0 }} />
                    <span className="text-sm font-medium" style={{ color: '#92400e' }}>
                      {summary.expired_count} expired quote{summary.expired_count !== 1 ? 's' : ''} — excluded from all figures above
                    </span>
                  </div>
                  <span className="text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: '#78350f' }}>
                    Value: {fmtMo(summary.expired_value_gbp || 0)} GBP
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>Quote Volume — Last 12 Months</p>
                    <div className="flex items-end gap-1.5 h-36">
                      {overTime.map((d, i) => {
                        const maxC = Math.max(...overTime.map(x => x.count), 1);
                        const h = Math.round((d.count / maxC) * 100);
                        const ah = d.count > 0 ? Math.round((d.accepted / d.count) * h) : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center">
                            <div className="w-full relative" style={{ height: '120px' }}>
                              <div className="absolute bottom-0 left-0 right-0 rounded-t" style={{ height: `${h}%`, background: 'hsl(var(--muted))' }} />
                              <div className="absolute bottom-0 left-0 right-0 rounded-t" style={{ height: `${ah}%`, background: '#16a34a' }} />
                            </div>
                            <p style={{ fontSize: '9px', color: 'hsl(var(--muted-fg))' }}>{d.month_label?.split(' ')[0]}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-4 mt-2">
                      <div className="flex items-center gap-1.5 text-xs"><div className="w-3 h-3 rounded" style={{ background: 'hsl(var(--muted))' }} /><span style={{ color: 'hsl(var(--muted-fg))' }}>Total</span></div>
                      <div className="flex items-center gap-1.5 text-xs"><div className="w-3 h-3 rounded" style={{ background: '#16a34a' }} /><span style={{ color: 'hsl(var(--muted-fg))' }}>Won</span></div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>Status Distribution</p>
                    <div className="space-y-2">
                      {byStatus.map(d => (
                        <div key={d.status} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[d.status] || '#94a3b8' }} />
                          <p className="text-xs w-20 flex-shrink-0" style={{ color: 'hsl(var(--muted-fg))' }}>{d.status}</p>
                          <div className="flex-1 h-4 rounded overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
                            <div className="h-full rounded transition-all"
                              style={{ width: `${total > 0 ? (d.count / total) * 100 : 0}%`, background: STATUS_COLORS[d.status] || '#94a3b8' }} />
                          </div>
                          <span className="text-xs font-bold tabular-nums w-6 text-right" style={{ color: NAVY }}>{d.count}</span>
                          <span className="text-xs w-10 text-right" style={{ color: 'hsl(var(--muted-fg))' }}>{total > 0 ? Math.round((d.count / total) * 100) : 0}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )
        )}

        {/* ══ PIPELINE ══ */}
        {!loading && activeTab === 'pipeline' && (
          <div className="space-y-5 max-w-5xl">
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={TrendingUp}   label="Open Pipeline (GBP)" value={fmtMo(summary?.pipeline_gbp || 0)}   sub="DRAFT + SENT quotes only" accent={NAVY}     testId="rpt-open-pipeline" />
              <KpiCard icon={CheckCircle2} label="Won Value (GBP)"     value={fmtMo(summary?.won_gbp || 0)}        sub="ACCEPTED quotes"          accent="#16a34a" testId="rpt-won-gbp-pipeline" />
              <KpiCard icon={FileText}     label="Open Quotes"         value={(summary?.by_status?.DRAFT || 0) + (summary?.by_status?.SENT || 0)} sub="Awaiting decision"   accent="#d97706" testId="rpt-open-count" />
              <KpiCard icon={Clock}        label="Win Rate"            value={total > 0 ? `${Math.round((accepted/total)*100)}%` : '—'} sub={`${accepted} won of ${total} total`} accent="#0e7490" testId="rpt-win-rate-pipeline" />
            </div>

            {/* Currency breakdown table */}
            {byCurrency.length > 0 && (
              <div className="rounded-xl border bg-white overflow-hidden">
                <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Value by Currency (Monthly)</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: NAVY, color: 'white' }}>
                      <th className="text-left px-4 py-2 text-xs font-bold">Currency</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Open Quotes</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Open Pipeline</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Won Quotes</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Won Value</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Won (GBP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCurrency.map((d, i) => {
                      const sym = d.currency === 'GBP' ? '£' : d.currency === 'EUR' ? '€' : d.currency === 'USD' ? '$' : d.currency + ' ';
                      const fmtLocal = (n) => `${sym}${(n||0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}/mo`;
                      return (
                        <tr key={d.currency} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                          <td className="px-4 py-2.5 font-bold text-sm" style={{ color: NAVY }}>{d.currency}</td>
                          <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#d97706' }}>{d.open_count}</td>
                          <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: d.open_value > 0 ? NAVY : 'hsl(var(--muted-fg))' }}>
                            {d.open_value > 0 ? fmtLocal(d.open_value) : '—'}
                          </td>
                          <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#16a34a' }}>{d.accepted_count}</td>
                          <td className="text-right px-4 py-2.5 text-sm tabular-nums" style={{ color: '#16a34a' }}>{fmtLocal(d.accepted_value)}</td>
                          <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>£{(d.accepted_value_gbp||0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}/mo</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Product breakdown */}
            {byProduct.length > 0 && (
              <div className="rounded-xl border bg-white overflow-hidden">
                <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Quotes by Product</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: NAVY, color: 'white' }}>
                      <th className="text-left px-4 py-2 text-xs font-bold">Product</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Total</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Open</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Won</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Open Pipeline (GBP)</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Won Value (GBP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byProduct.map((d, i) => (
                      <tr key={d.product_code} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                        <td className="px-4 py-2.5 font-medium text-sm" style={{ color: NAVY }}>{PRODUCT_LABELS[d.product_code] || d.product_code}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums">{d.count}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#d97706' }}>{d.open_count}</td>
                        <td className="text-center px-3 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{d.accepted}</td>
                        <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: d.pipeline_gbp > 0 ? NAVY : 'hsl(var(--muted-fg))' }}>
                          {d.pipeline_gbp > 0 ? fmt(d.pipeline_gbp) + '/mo' : '—'}
                        </td>
                        <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{fmt(d.won_gbp)}/mo</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══ CONTRACTS MRR ══ */}
        {!loading && activeTab === 'contracts' && (
          <div className="space-y-5 max-w-5xl">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard icon={DollarSign}   label="Total MRR (GBP)"       value={fmtMo(contracts?.mrr_gbp || 0)}                  sub="Monthly recurring revenue"         accent="#0e7490" testId="rpt-mrr" />
              <KpiCard icon={FileText}     label="Live Contracts"         value={contracts?.total_live_contracts || 0}             sub="Currently active"                  accent="#16a34a" testId="rpt-live-contracts" />
              <KpiCard icon={TrendingUp}   label="Annual Run Rate (GBP)"  value={fmt((contracts?.mrr_gbp || 0) * 12)}             sub="MRR × 12"                          accent={NAVY}    testId="rpt-arr" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contracts?.by_product?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>MRR by Product</p>
                    <div className="space-y-2.5">
                      {contracts.by_product.map((p, i) => {
                        const maxV = Math.max(...contracts.by_product.map(x => x.mrr_gbp), 1);
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <p className="text-xs w-24 flex-shrink-0" style={{ color: 'hsl(var(--muted-fg))' }}>{PRODUCT_LABELS[p.product_code] || p.product_code}</p>
                            <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
                              <div className="h-full rounded" style={{ width: `${(p.mrr_gbp / maxV) * 100}%`, background: '#0e7490' }} />
                            </div>
                            <span className="text-xs font-bold tabular-nums w-20 text-right" style={{ color: '#0e7490' }}>{fmtMo(p.mrr_gbp)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {contracts?.by_psm?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>MRR by PSM</p>
                    <div className="space-y-2.5">
                      {contracts.by_psm.map((p, i) => {
                        const maxV = Math.max(...contracts.by_psm.map(x => x.mrr_gbp), 1);
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <p className="text-xs w-24 flex-shrink-0 truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{p.psm_name}</p>
                            <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
                              <div className="h-full rounded" style={{ width: `${(p.mrr_gbp / maxV) * 100}%`, background: '#0e7490' }} />
                            </div>
                            <span className="text-xs font-bold tabular-nums w-20 text-right" style={{ color: '#0e7490' }}>{fmtMo(p.mrr_gbp)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* ══ EXPIRING ══ */}
        {!loading && activeTab === 'expiring' && (
          <div className="space-y-4 max-w-4xl">
            {expiring.length === 0 ? (
              <div className="rounded-xl border bg-white p-12 text-center">
                <CheckCircle2 size={32} className="mx-auto mb-3" style={{ color: '#16a34a' }} />
                <p className="font-semibold" style={{ color: NAVY }}>No quotes expiring in the next 30 days</p>
              </div>
            ) : (
              <div className="rounded-xl border bg-white overflow-hidden">
                <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {expiring.length} quote{expiring.length !== 1 ? 's' : ''} expiring within 30 days
                  </p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: NAVY, color: 'white' }}>
                      <th className="text-left px-4 py-2 text-xs font-bold">Quote</th>
                      <th className="text-left px-4 py-2 text-xs font-bold">Partner</th>
                      <th className="text-left px-4 py-2 text-xs font-bold">Tenant</th>
                      {effectiveTeamMode && <th className="text-left px-4 py-2 text-xs font-bold">PSM</th>}
                      <th className="text-center px-3 py-2 text-xs font-bold">Status</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Expiry</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Days Left</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Monthly Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiring.map((q, i) => {
                      const urgent = q.days_left <= 7;
                      const sym = q.currency === 'GBP' ? '£' : q.currency === 'EUR' ? '€' : q.currency === 'USD' ? '$' : q.currency + ' ';
                      return (
                        <tr key={q.id} style={{ background: urgent ? '#fef2f2' : i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                          <td className="px-4 py-2.5">
                            <Link to={`/quotes/${q.id}`} className="font-mono text-xs font-bold hover:underline" style={{ color: NAVY }}>{q.quote_number}</Link>
                          </td>
                          <td className="px-4 py-2.5 text-sm font-medium" style={{ color: NAVY }}>{q.customer_name || '—'}</td>
                          <td className="px-4 py-2.5 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{q.tenant_name || <span style={{ opacity: 0.4 }}>—</span>}</td>
                          {effectiveTeamMode && <td className="px-4 py-2.5 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{q.psm_name}</td>}
                          <td className="text-center px-3 py-2.5">
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                              style={{ background: STATUS_COLORS[q.status] + '20', color: STATUS_COLORS[q.status] }}>{q.status}</span>
                          </td>
                          <td className="text-center px-3 py-2.5 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{q.expiry_date}</td>
                          <td className="text-center px-3 py-2.5">
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded"
                              style={{ background: urgent ? '#fee2e2' : '#fef3c7', color: urgent ? '#991b1b' : '#92400e' }}>
                              {q.days_left}d
                            </span>
                          </td>
                          <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: NAVY }}>
                            {sym}{(q.monthly_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══ GEOGRAPHY ══ */}
        {!loading && activeTab === 'geography' && (
          <div className="space-y-6 max-w-5xl">
            <GeoTable
              title="By AWS Deployment Region"
              subtitle="Quotes and live contracts grouped by the AWS region the solution is deployed in"
              rows={byRegion}
              labelKey="region"
              labelHeader="Region"
              testId="geo-region-table"
            />
            <GeoTable
              title="By Partner / Customer Country"
              subtitle="Quotes and live contracts grouped by the partner's registered country"
              rows={byCountry}
              labelKey="country"
              labelHeader="Country"
              testId="geo-country-table"
            />
          </div>
        )}

        {/* ══ TEAM ══ */}
        {!loading && activeTab === 'team' && effectiveTeamMode && (
          <div className="space-y-4 max-w-5xl">
            {byUser.length === 0 ? (
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No team data available.</p>
            ) : (
              <div className="rounded-xl border bg-white overflow-hidden">
                <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Team Performance — All Time</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: NAVY, color: 'white' }}>
                      <th className="text-left px-4 py-2 text-xs font-bold">PSM</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Total</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Draft</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Sent</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Won</th>
                      <th className="text-center px-3 py-2 text-xs font-bold">Win Rate</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Pipeline GBP/mo</th>
                      <th className="text-right px-4 py-2 text-xs font-bold">Won GBP/mo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser.map((u, i) => (
                      <tr key={u.user_id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                        <td className="px-4 py-2.5 font-medium text-sm" style={{ color: NAVY }}>{u.user_name}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums">{u.total}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#94a3b8' }}>{u.draft}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#3b82f6' }}>{u.sent}</td>
                        <td className="text-center px-3 py-2.5 text-sm tabular-nums font-bold" style={{ color: '#16a34a' }}>{u.accepted}</td>
                        <td className="text-center px-3 py-2.5">
                          <span className="text-xs font-bold" style={{ color: u.conversion_rate >= 0.5 ? '#16a34a' : '#d97706' }}>
                            {Math.round((u.conversion_rate || 0) * 100)}%
                          </span>                        </td>
                        <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: NAVY }}>{fmt(u.pipeline_gbp)}</td>
                        <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{fmt(u.won_gbp)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: NAVY, color: 'white' }}>
                      <td className="px-4 py-2.5 text-xs font-bold">TOTAL</td>
                      <td className="text-center px-3 py-2.5 text-xs font-bold tabular-nums">{byUser.reduce((a, u) => a + u.total, 0)}</td>
                      <td className="text-center px-3 py-2.5 text-xs tabular-nums">{byUser.reduce((a, u) => a + u.draft, 0)}</td>
                      <td className="text-center px-3 py-2.5 text-xs tabular-nums">{byUser.reduce((a, u) => a + u.sent, 0)}</td>
                      <td className="text-center px-3 py-2.5 text-xs font-bold tabular-nums">{byUser.reduce((a, u) => a + u.accepted, 0)}</td>
                      <td className="text-center px-3 py-2.5 text-xs"></td>
                      <td className="text-right px-4 py-2.5 text-xs font-bold tabular-nums">{fmt(byUser.reduce((a, u) => a + u.pipeline_gbp, 0))}</td>
                      <td className="text-right px-4 py-2.5 text-xs font-bold tabular-nums">{fmt(byUser.reduce((a, u) => a + u.won_gbp, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
